import React, { useState } from 'react'
import { sectionService } from 'features/dev/api';
import { useStateContext } from 'shared/contexts/ContextProvider';
import { GripVertical, Edit2, Save, X, Plus, Trash2 } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { id } from 'date-fns/locale';

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const SectionItem = ({ item }) => {
  const { token, showToast, currentColor } = useStateContext();
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    name: item.name,
    url: item.url,
    priority: item.priority,
  });
  
  const [subSections, setSubSections] = useState(
    item.sub_sections?.rows?.map((sub, idx) => ({
      ...sub,
      _order: idx,
    })) || []
  );

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubSectionChange = (id, field, value) => {
    setSubSections((prev) =>
      prev.map((sub) => (sub.id === id ? { ...sub, [field]: value } : sub))
    );
  };

  const handleAddSubSection = () => {
    const newId = Math.min(...subSections.map(s => s.id), 0) - 1;
    const newSubSection = {
      id: newId,
      name: 'Новое подразделение',
      url: '',
      icon: '',
      priority: subSections.length > 0 ? Math.max(...subSections.map(s => s.priority)) + 1 : 1,
      _isNew: true,
      _order: subSections.length,
    };
    setSubSections([...subSections, newSubSection]);
  };

  const handleRemoveSubSection = (id) => {
    setSubSections((prev) => prev.filter((sub) => sub.id !== id));
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const items = Array.from(subSections);
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);

    // Update priority values based on order
    const updatedItems = items.map((item, idx) => ({
      ...item,
      priority: idx + 1,
      _order: idx,
    }));

    setSubSections(updatedItems);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Update main section data
      const data = {
        id: item.id,
        ...formData,
      };
      const response = await sectionService.save(
        data,
        token
      );

      if (response.status !== 0) {
        setErrors(response.response || {});
        showToast({
          title: 'Ошибка',
          content: response.message || 'Не удалось обновить раздел',
          type: 'error',
        });
        setLoading(false);
        return;
      }

      // Update subsections
      const subsectionUpdateData = subSections.map((sub) => ({
        id: sub.id,
        name: sub.name,
        url: sub.url,
        icon: sub.icon,
        priority: sub.priority,
      }));

      const subsectionResponse = await sectionService.updateSubSections(
        item.id,
        subsectionUpdateData,
        token
      );

      if (subsectionResponse.status !== 0) {
        showToast({
          title: 'Ошибка',
          content: subsectionResponse.message || 'Не удалось обновить подразделы',
          type: 'error',
        });
        setLoading(false);
        return;
      }

      showToast({
        title: 'Успешно!',
        content: 'Раздел и подразделы обновлены',
        type: 'success',
      });

      setIsEditMode(false);
    } catch (error) {
      showToast({
        title: 'Ошибка!',
        content: error.message,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isEditMode) {
    // View Mode
    return (
      <div className='p-8 bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-lg'>
        <div className='flex items-center justify-between mb-6'>
          <h2 className='text-2xl font-bold text-gray-900'>Раздел: {item.name}</h2>
          <button
            onClick={() => setIsEditMode(true)}
            className='flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all duration-200 hover:shadow-md'
            style={{ backgroundColor: currentColor }}
          >
            <Edit2 size={16} />
            Редактировать
          </button>
        </div>

        <div className='grid grid-cols-2 gap-6 mb-8'>
          <div className='bg-white p-4 rounded-lg border border-gray-200'>
            <p className='text-sm text-gray-500 font-semibold uppercase tracking-wide'>ID</p>
            <p className='text-2xl font-bold text-gray-900'>{item.id}</p>
          </div>
          <div className='bg-white p-4 rounded-lg border border-gray-200'>
            <p className='text-sm text-gray-500 font-semibold uppercase tracking-wide'>URL</p>
            <p className='text-lg font-mono text-blue-600 break-all'>{item.url}</p>
          </div>
          <div className='bg-white p-4 rounded-lg border border-gray-200'>
            <p className='text-sm text-gray-500 font-semibold uppercase tracking-wide'>Приоритет</p>
            <p className='text-2xl font-bold text-gray-900'>{item.priority}</p>
          </div>
          <div className='bg-white p-4 rounded-lg border border-gray-200'>
            <p className='text-sm text-gray-500 font-semibold uppercase tracking-wide'>Подразделов</p>
            <p className='text-2xl font-bold text-gray-900'>{item.sub_sections?.meta?.size || 0}</p>
          </div>
        </div>

        <div className='mb-6'>
          <h3 className='text-lg font-bold text-gray-900 mb-4'>Подразделы</h3>
          <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
            {item.sub_sections?.rows?.length > 0 ? (
              <div className='divide-y divide-gray-200'>
                {item.sub_sections.rows.map((sub, idx) => (
                  <div key={sub.id} className='p-4 hover:bg-gray-50 transition-colors'>
                    <div className='flex items-start justify-between'>
                      <div className='flex-1'>
                        <div className='flex items-center gap-3 mb-2'>
                          <span className='inline-block w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-white text-xs flex items-center justify-center font-bold'>
                            {idx + 1}
                          </span>
                          <p className='font-semibold text-gray-900'>{sub.name}</p>
                        </div>
                        <p className='text-sm text-gray-600 ml-9'>
                          <span className='font-mono text-blue-600'>{sub.url}</span>
                        </p>
                        <div className='flex gap-4 mt-2 ml-9 text-xs text-gray-500'>
                          <span>Icon: {sub.icon}</span>
                          <span>Priority: {sub.priority}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='p-8 text-center text-gray-500'>
                <p>Нет подразделов</p>
              </div>
            )}
          </div>
        </div>

        <div className='grid grid-cols-2 gap-4 text-sm'>
          <div className='bg-gray-100 p-4 rounded-lg'>
            <p className='text-gray-500 font-semibold mb-1'>Создан</p>
            <p className='text-gray-700'>{formatDate(item.created)}</p>
          </div>
          <div className='bg-gray-100 p-4 rounded-lg'>
            <p className='text-gray-500 font-semibold mb-1'>Обновлен</p>
            <p className='text-gray-700'>{formatDate(item.updated)}</p>
          </div>
        </div>
      </div>
    );
  }

  // Edit Mode
  return (
    <div className='p-8 bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-lg'>
      <div className='flex items-center justify-between mb-6'>
        <h2 className='text-2xl font-bold text-gray-900'>Редактировать раздел</h2>
        <button
          onClick={() => setIsEditMode(false)}
          className='p-2 hover:bg-gray-200 rounded-lg transition-colors'
        >
          <X size={20} className='text-gray-600' />
        </button>
      </div>

      <div className='bg-white rounded-lg border border-gray-200 p-6 mb-6'>
        <h3 className='text-lg font-bold text-gray-900 mb-4'>Основная информация</h3>
        
        <div className='space-y-4'>
          <div>
            <label className='block text-sm font-semibold text-gray-700 mb-2'>
              Название
            </label>
            <input
              type='text'
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
              placeholder='Введите название'
            />
            {errors.name && <p className='text-red-500 text-sm mt-1'>{errors.name}</p>}
          </div>

          <div>
            <label className='block text-sm font-semibold text-gray-700 mb-2'>
              URL
            </label>
            <input
              type='text'
              value={formData.url}
              onChange={(e) => handleInputChange('url', e.target.value)}
              className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
              placeholder='Введите URL'
            />
            {errors.url && <p className='text-red-500 text-sm mt-1'>{errors.url}</p>}
          </div>

          <div>
            <label className='block text-sm font-semibold text-gray-700 mb-2'>
              Приоритет
            </label>
            <input
              type='number'
              value={formData.priority}
              onChange={(e) => handleInputChange('priority', parseInt(e.target.value))}
              className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
              placeholder='Введите приоритет'
            />
            {errors.priority && <p className='text-red-500 text-sm mt-1'>{errors.priority}</p>}
          </div>
        </div>
      </div>

      <div className='bg-white rounded-lg border border-gray-200 p-6 mb-6'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-bold text-gray-900'>Подразделы</h3>
          <button
            onClick={handleAddSubSection}
            className='flex items-center gap-2 px-3 py-1.5 rounded-lg text-white text-sm transition-all'
            style={{ backgroundColor: currentColor }}
          >
            <Plus size={16} />
            Добавить
          </button>
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId='subsections'>
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className={`space-y-3 p-4 rounded-lg transition-colors ${
                  snapshot.isDraggingOver ? 'bg-blue-50 border-2 border-blue-300' : 'bg-gray-50 border border-gray-200'
                }`}
              >
                {subSections.length > 0 ? (
                  subSections.map((sub, idx) => (
                    <Draggable key={sub.id} draggableId={`sub-${sub.id}`} index={idx}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`bg-white rounded-lg border-2 p-4 transition-all ${
                            snapshot.isDragging
                              ? 'border-blue-500 shadow-lg'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className='flex gap-3'>
                            <div {...provided.dragHandleProps} className='flex items-center justify-center'>
                              <GripVertical size={20} className='text-gray-400 hover:text-gray-600' />
                            </div>

                            <div className='flex-1 space-y-3'>
                              <input
                                type='text'
                                value={sub.name}
                                onChange={(e) => handleSubSectionChange(sub.id, 'name', e.target.value)}
                                placeholder='Название подраздела'
                                className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
                              />

                              <div className='grid grid-cols-3 gap-3'>
                                <input
                                  type='text'
                                  value={sub.url}
                                  onChange={(e) => handleSubSectionChange(sub.id, 'url', e.target.value)}
                                  placeholder='URL'
                                  className='px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
                                />
                                <input
                                  type='text'
                                  value={sub.icon}
                                  onChange={(e) => handleSubSectionChange(sub.id, 'icon', e.target.value)}
                                  placeholder='Icon'
                                  className='px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
                                />
                                <input
                                  type='number'
                                  value={sub.priority}
                                  onChange={(e) => handleSubSectionChange(sub.id, 'priority', parseInt(e.target.value))}
                                  placeholder='Priority'
                                  className='px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
                                />
                              </div>
                            </div>

                            <button
                              onClick={() => handleRemoveSubSection(sub.id)}
                              className='p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors'
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))
                ) : (
                  <div className='py-8 text-center text-gray-500'>
                    <p>Нет подразделов. Добавьте новое подразделение.</p>
                  </div>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      <div className='flex gap-3 justify-end'>
        <button
          onClick={() => setIsEditMode(false)}
          className='px-6 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition-colors'
        >
          Отмена
        </button>
        <button
          onClick={handleSave}
          disabled={loading}
          className='flex items-center gap-2 px-6 py-2 rounded-lg text-white font-semibold transition-all disabled:opacity-50'
          style={{ backgroundColor: currentColor }}
        >
          <Save size={16} />
          {loading ? 'Сохранение...' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
};

export default SectionItem